import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSeoSettingByPath } from '@/hooks/useSeoSettings';

const SITE_URL = 'https://brantbooker.com';

interface PageSeoProps {
  defaultTitle?: string;
  defaultDescription?: string;
  defaultKeywords?: string;
  defaultOgImage?: string;
  canonicalPath?: string; // Optional override for canonical URL
}

const PageSeo = ({ 
  defaultTitle, 
  defaultDescription, 
  defaultKeywords,
  defaultOgImage = '/og-image.png',
  canonicalPath
}: PageSeoProps) => {
  const location = useLocation();
  const { data: seoSettings } = useSeoSettingByPath(location.pathname);

  useEffect(() => {
    // Use database settings if available, otherwise use defaults
    const title = seoSettings?.title || defaultTitle;
    const description = seoSettings?.description || defaultDescription;
    const keywords = seoSettings?.keywords || defaultKeywords;
    const ogTitle = seoSettings?.og_title || title;
    const ogDescription = seoSettings?.og_description || description;
    const ogImage = seoSettings?.og_image || defaultOgImage;
    
    // Canonical URL - use override if provided, otherwise use current path
    const canonicalUrl = `${SITE_URL}${canonicalPath || location.pathname}`;

    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMeta = (name: string, content: string | null | undefined, property?: boolean) => {
      if (!content) return;
      
      const selector = property 
        ? `meta[property="${name}"]` 
        : `meta[name="${name}"]`;
      
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Helper function to update or create link tag
    const updateLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      
      link.setAttribute('href', href);
    };

    // Update meta tags
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    
    // Update Open Graph tags
    updateMeta('og:title', ogTitle, true);
    updateMeta('og:description', ogDescription, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:url', canonicalUrl, true);
    
    // Update Twitter Card tags
    updateMeta('twitter:title', ogTitle);
    updateMeta('twitter:description', ogDescription);
    updateMeta('twitter:image', ogImage);

    // Update canonical URL
    updateLink('canonical', canonicalUrl);

    // Cleanup function to restore original title on unmount
    return () => {
      // We don't reset here as the next page will set its own
    };
  }, [seoSettings, defaultTitle, defaultDescription, defaultKeywords, defaultOgImage, canonicalPath, location.pathname]);

  return null;
};

export default PageSeo;
